import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Select,
  Button,
  Card,
  Space,
  message,
  Spin,
  Radio,
  Divider,
} from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { StoreGroup, StoreGroupFormProps, Store } from '../../types/storeGroup';
import StoreGroupService from '../../services/storeGroupService';
import HQDisplayGroupService from '../../services/hqDisplayGroupService';
import { warehouseService } from '../../services/warehouseService';
import StoreSelection from './components/StoreSelection';

const { Option } = Select;
const { TextArea } = Input;

const StoreGroupForm: React.FC<StoreGroupFormProps> = ({ mode }) => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [displayGroups, setDisplayGroups] = useState<{ code: string; name: string }[]>([]);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [typicalStoreCode, setTypicalStoreCode] = useState<string>('');

  const storeGroupService = StoreGroupService;
  const hqDisplayGroupService = HQDisplayGroupService;

  // 将 warehouse 数据转换为 Store 格式的辅助函数
  const convertWarehouseToStore = (warehouse: any): Store => {
    return {
      id: warehouse.id ? warehouse.id.toString() : '',
      code: warehouse.code || '',
      name: warehouse.name || '',
      detailAddress: warehouse.detailAddress || warehouse.address || '',
      area: warehouse.area || warehouse.region || '',
      city: warehouse.city || '',
      format: warehouse.format || warehouse.type || 'Standard',
      clusterSection: warehouse.clusterSection || '',
      size: warehouse.size || 0,
      contact: warehouse.contactName || warehouse.contact || '',
      phone: warehouse.contactPhone || warehouse.phone || '',
      status: warehouse.status === 'ACTIVE' ? 'Active' : 
              warehouse.status === 'INACTIVE' ? 'Inactive' : 
              warehouse.status || 'Active'
    };
  };

  useEffect(() => {
    fetchDisplayGroups();
    if (mode === 'edit' && code) {
      fetchStoreGroupDetail(code);
    }
  }, [mode, code]);

  const fetchDisplayGroups = async () => {
    try {
      const response = await hqDisplayGroupService.getDisplayGroups(
        { status: 'ENABLE' }
      );
      if (response.success && response.data) {
        setDisplayGroups(response.data.map((group: any) => ({
          code: group.code,
          name: group.name,
        })));
      } else {
        setDisplayGroups([]);
      }
    } catch (error) {
      message.error(t('storeGroup.fetchDisplayGroupsFailed'));
      console.error('Failed to fetch display group list:', error);
    }
  };

  const fetchStoreGroupDetail = async (code: string) => {
    setLoading(true);
    try {
      const response = await storeGroupService.getStoreGroupByCode(code);
      if (response.success && response.data) {
        const storeGroup = response.data;
        form.setFieldsValue({
          code: storeGroup.code,
          name: storeGroup.name,
          displayGroupCode: storeGroup.displayGroupCode,
          status: storeGroup.status,
          remark: storeGroup.remark,
        });
        
        // 获取门店详情，通过shopCodes获取门店列表
        if (storeGroup.shopCodes && storeGroup.shopCodes.length > 0) {
          try {
            // 使用warehouseService.listWarehouse方法获取所有门店，然后过滤出属于当前门店组的门店
            const storesResponse = await warehouseService.listWarehouse({});
            if (storesResponse.success && storesResponse.data) {
              // 转换数据格式
              const allStores = storesResponse.data.map(convertWarehouseToStore);
              const filteredStores = allStores.filter(store => 
                storeGroup.shopCodes.includes(store.code)
              );
              setSelectedStores(filteredStores);
            } else {
              message.error(storesResponse.errorMessage || t('storeGroup.fetchStoresFailed'));
              setSelectedStores([]);
            }
          } catch (storeError) {
            console.error('Failed to fetch store list:', storeError);
            setSelectedStores([]);
          }
        } else {
          setSelectedStores([]);
        }
        
        setTypicalStoreCode(storeGroup.typicalStoreCode || '');
      } else {
        message.error(response.errorMessage || t('storeGroup.fetchDetailFailed'));
      }
    } catch (error) {
      message.error(t('storeGroup.fetchDetailFailed'));
      console.error('Failed to fetch store group details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (selectedStores.length === 0) {
      message.error(t('storeGroup.selectAtLeastOneStore'));
      return;
    }

    if (!typicalStoreCode) {
      message.error(t('storeGroup.selectRepresentativeStore'));
      return;
    }

    setSubmitting(true);
    try {
      const storeIds = selectedStores.map(store => store.code);
      
      if (mode === 'create') {
        // 创建模式需要完整的StoreGroup对象
        const now = new Date().toISOString().replace('T', ' ').substring(0, 19);
        const storeGroupData: StoreGroup = {
          ...values,
          shopCodes: storeIds,
          typicalStoreCode,
          creatorName: 'current_user', // Assume current user
          gmtCreate: now
        };
        let res = await storeGroupService.createStoreGroup(storeGroupData);
        
        if (res.success) {
          message.success(t('common.success'));
          navigate('/store-group/list');
        } else {
          message.error(res.errorMessage);
        }
      } else {
        // Update mode can use Partial<StoreGroup>
        const storeGroupData: Partial<StoreGroup> = {
          ...values,
          shopCodes: storeIds,
          typicalStoreCode,
        };
        let res = await storeGroupService.updateStoreGroup(code!, storeGroupData);
        if (res.success) {
          message.success(t('common.success'));
          navigate('/store-group/list');
        } else {
          message.error(res.errorMessage);
        }
      } 
    } catch (error: any) {
      message.error(t(mode === 'create' ? 'common.createFailed' : 'common.updateFailed', { error: error.message }));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/store-group/list');
  };

  const handleStoreSelect = (stores: Store[]) => {
    setSelectedStores(stores);
    // 如果当前选择的代表门店不在选中的门店列表中，清空代表门店
    if (typicalStoreCode && !stores.some(store => store.code === typicalStoreCode)) {
      setTypicalStoreCode('');
    }
  };

  const handleTypicalStoreSelect = (storeId: string) => {
    setTypicalStoreCode(storeId);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="store-group-form">
      <div className="page-header">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
            {t('common.back')}
          </Button>
          <h1>{mode === 'create' ? t('storeGroup.createTitle') : t('storeGroup.editTitle')}</h1>
        </Space>
      </div>

      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          // initialValues={{
          //   status: 'Active',
          // }}
        >
          {mode === 'edit' && (
          <Form.Item
            name="code"
            label={t('storeGroup.code')}
            rules={[
              { required: true, message: t('storeGroup.codeRequired') },
              { pattern: /^[A-Za-z0-9_-]{2,20}$/, message: t('storeGroup.codePattern') },
            ]}
          >
            <Input
              placeholder={t('storeGroup.codePlaceholder')}
              disabled={mode === 'edit'}
              maxLength={20}
            />
          </Form.Item>
        )}
          

          <Form.Item
            name="name"
            label={t('storeGroup.name')}
            rules={[
              { required: true, message: t('storeGroup.nameRequired') },
              { max: 50, message: t('storeGroup.nameMaxLength') },
            ]}
          >
            <Input placeholder={t('storeGroup.namePlaceholder')} maxLength={50} />
          </Form.Item>

          <Form.Item
            name="displayGroupCode"
            label={t('storeGroup.relatedDisplayGroup')}
            rules={[{ required: true, message: t('storeGroup.displayGroupRequired') }]}
          >
            <Select
              placeholder={t('storeGroup.displayGroupPlaceholder')}
              showSearch
              disabled={mode === 'edit'}
              optionFilterProp="children"
            >
              {displayGroups.map((group) => (
                <Option key={group.code} value={group.code}>
                  {`${group.code} - ${group.name}`}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* <Form.Item
            name="status"
            label={t('common.status')}
            rules={[{ required: true, message: t('common.statusRequired') }]}
          >
            <Radio.Group>
              <Radio value="ENABLE">{t('common.active')}</Radio>
              <Radio value="DISABLE">{t('common.inactive')}</Radio>
            </Radio.Group>
          </Form.Item> */}

          {/* <Divider orientation="left">{t('storeGroup.storeSelection')}</Divider> */}

          <StoreSelection
            selectedStores={selectedStores}
            onStoreSelect={handleStoreSelect}
            typicalStoreCode={typicalStoreCode}
            onTypicalStoreSelect={handleTypicalStoreSelect}
          />

          <Form.Item
            name="remark"
            label={t('common.remark')}
            rules={[{ max: 200, message: t('common.remarkMaxLength') }]}
          >
            <TextArea
              placeholder={t('common.remarkPlaceholder')}
              autoSize={{ minRows: 3, maxRows: 6 }}
              maxLength={200}
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={submitting}
              >
                {t('common.save')}
              </Button>
              <Button onClick={handleBack}>{t('common.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default StoreGroupForm;